/**
 * Premium Page
 *
 * Displays Premium benefits and allows users to upgrade.
 * Shows pricing from PayPal configuration (monthly and annual).
 * Uses UpgradeModal for PayPal payment integration.
 *
 * @module pages/PremiumPage
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Card, Button } from '../components/ui';
import { UserRole } from '@budgira/shared';
import { publicConfigService, type PublicConfig } from '../services/public-config.service';
import { UpgradeModal } from '../components/subscription';

// Icons
const CheckIcon = ({ className = '' }: { className?: string }) => (
  <svg className={`w-5 h-5 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const CrossIcon = () => (
  <svg className="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CrownIcon = () => (
  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
    <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z" />
  </svg>
);

const StarIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const CreditCardIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

type PlanType = 'monthly' | 'annual';

const PremiumPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('annual');
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load public config on mount
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await publicConfigService.get();
      if (response.success && response.data) {
        setConfig(response.data);
      }
    } catch (err) {
      console.error('Failed to load config:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const isPremiumOrAdmin = user?.role === UserRole.PREMIUM || user?.role === UserRole.ADMIN;

  // Format price for display
  const formatPrice = (price: number) => {
    return price.toFixed(2).replace('.', ',');
  };

  // Get prices from config
  const monthlyPrice = config?.subscription.monthlyPrice ?? 0.49;
  const annualMonthlyEquivalent = config?.subscription.annualMonthlyEquivalent ?? 0.35;
  const annualPrice = config?.subscription.annualPrice ?? 4.2;
  const annualSavingsPercent = config?.subscription.annualSavingsPercent ?? 29;
  const operationLimit = config?.freeAccount.operationLimit ?? 50;
  const accountLimit = config?.freeAccount.accountLimit ?? 1;

  // Calculate savings
  const yearlySavings = (monthlyPrice * 12) - annualPrice;

  const handleUpgradeClick = () => {
    setShowUpgradeModal(true);
  };

  const handleUpgradeSuccess = () => {
    setShowUpgradeModal(false);
    navigate('/settings', { state: { upgraded: true } });
  };

  const handleUpgradeClose = () => {
    setShowUpgradeModal(false);
  };

  // Features for free plan
  const freeFeatures = [
    { key: 'accounts', value: `${accountLimit}`, included: true },
    { key: 'operations', value: `${operationLimit}`, included: true },
    { key: 'dashboard', included: true },
    { key: 'categories', included: true },
    { key: 'paymentMethods', included: true },
    { key: 'schedulers', included: false },
    { key: 'sharedAccess', included: false },
    { key: 'import', included: false },
    { key: 'export', included: false },
  ];

  // Features for premium plan
  const premiumFeatures = [
    { key: 'accounts', value: t('premium.features.unlimited'), included: true },
    { key: 'operations', value: t('premium.features.unlimited'), included: true },
    { key: 'dashboardAdvanced', included: true },
    { key: 'categoriesCustom', included: true },
    { key: 'paymentMethodsCustom', included: true },
    { key: 'schedulers', included: true },
    { key: 'sharedAccess', included: true },
    { key: 'import', included: true },
    { key: 'export', included: true },
  ];

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="h-16 bg-slate-200 dark:bg-slate-700 rounded w-48 mx-auto animate-pulse" />
        <div className="grid md:grid-cols-2 gap-6">
          <div className="h-96 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse" />
          <div className="h-96 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl mb-4 text-white">
          <CrownIcon />
        </div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">{t('premium.title')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-2 text-lg">{t('premium.subtitle')}</p>
      </div>

      {/* Already Premium Banner */}
      {isPremiumOrAdmin && (
        <Card className="bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30 border-amber-200 dark:border-amber-700">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-12 h-12 bg-amber-500 rounded-xl text-white">
              <StarIcon />
            </div>
            <div>
              <h3 className="font-semibold text-amber-900 dark:text-amber-200">{t('premium.alreadyPremium.title')}</h3>
              <p className="text-amber-700 dark:text-amber-300 text-sm">{t('premium.alreadyPremium.message')}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Free Plan */}
        <Card className="relative">
          <div className="text-center pb-6 border-b border-slate-100 dark:border-slate-700">
            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
              {t('premium.plans.free.name')}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
              {t('premium.plans.free.description')}
            </p>
            <div className="mt-4">
              <span className="text-4xl font-bold text-slate-900 dark:text-slate-100">0</span>
              <span className="text-slate-500 dark:text-slate-400 ml-1">€</span>
            </div>
          </div>
          <ul className="mt-6 space-y-3">
            {freeFeatures.map((feature) => (
              <li key={feature.key} className="flex items-center gap-3">
                {feature.included ? (
                  <CheckIcon className="text-green-500 flex-shrink-0" />
                ) : (
                  <CrossIcon />
                )}
                <span className={feature.included ? 'text-slate-600 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500'}>
                  {feature.value && <span className="font-semibold">{feature.value}</span>}{' '}
                  {t(`premium.features.${feature.key}`)}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <Button variant="secondary" className="w-full" disabled>
              {t('premium.plans.free.currentPlan')}
            </Button>
          </div>
        </Card>

        {/* Premium Plan */}
        <Card className="relative border-2 border-violet-500 shadow-lg shadow-violet-100 dark:shadow-violet-900/30">
          {/* Popular badge */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="bg-violet-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              {t('premium.plans.premium.popular')}
            </span>
          </div>

          <div className="text-center pb-6 border-b border-slate-100 dark:border-slate-700">
            <h3 className="text-xl font-bold text-violet-600 dark:text-violet-400">
              {t('premium.plans.premium.name')}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
              {t('premium.plans.premium.description')}
            </p>

            {/* Pricing options */}
            <div className="mt-4 space-y-3">
              {/* Annual option - Recommended */}
              <button
                onClick={() => setSelectedPlan('annual')}
                disabled={isPremiumOrAdmin}
                className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                  selectedPlan === 'annual'
                    ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30'
                    : 'border-slate-200 dark:border-slate-700 hover:border-violet-300'
                } ${isPremiumOrAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarIcon />
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      {t('premium.plans.premium.annual')}
                    </span>
                  </div>
                  <span className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-xs font-bold px-2 py-1 rounded">
                    {t('premium.plans.premium.recommended')}
                  </span>
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-violet-600 dark:text-violet-400">
                    {formatPrice(annualPrice)}€
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">/{t('premium.year')}</span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {t('premium.plans.premium.annualEquivalent', { price: formatPrice(annualMonthlyEquivalent) })}
                </p>
                <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                  <CheckIcon className="inline w-4 h-4 mr-1" />
                  {t('premium.plans.premium.savings', { 
                    amount: formatPrice(yearlySavings), 
                    percent: annualSavingsPercent 
                  })}
                </p>
              </button>

              {/* Monthly option */}
              <button
                onClick={() => setSelectedPlan('monthly')}
                disabled={isPremiumOrAdmin}
                className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                  selectedPlan === 'monthly'
                    ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30'
                    : 'border-slate-200 dark:border-slate-700 hover:border-violet-300'
                } ${isPremiumOrAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="flex items-center gap-2">
                  <CreditCardIcon />
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {t('premium.plans.premium.monthly')}
                  </span>
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-slate-700 dark:text-slate-300">
                    {formatPrice(monthlyPrice)}€
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">/{t('premium.month')}</span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {t('premium.plans.premium.monthlyRenewal')}
                </p>
              </button>
            </div>
          </div>

          <ul className="mt-6 space-y-3">
            {premiumFeatures.map((feature) => (
              <li key={feature.key} className="flex items-center gap-3">
                <CheckIcon className="text-violet-500 flex-shrink-0" />
                <span className="text-slate-700 dark:text-slate-200 font-medium">
                  {feature.value && <span className="font-semibold">{feature.value}</span>}{' '}
                  {t(`premium.features.${feature.key}`)}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-8">
            {isPremiumOrAdmin ? (
              <Button className="w-full bg-green-500 hover:bg-green-600" disabled>
                <CheckIcon className="mr-2" />
                {t('premium.plans.premium.active')}
              </Button>
            ) : (
              <Button
                className="w-full bg-violet-600 hover:bg-violet-700"
                onClick={handleUpgradeClick}
              >
                {t('premium.plans.premium.upgrade')}
              </Button>
            )}
          </div>

          {/* Subscription info */}
          {!isPremiumOrAdmin && (
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center mt-4">
              {t('premium.plans.premium.subscriptionInfo')}
            </p>
          )}
        </Card>
      </div>

      {/* Benefits Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 text-center mb-8">
          {t('premium.benefits.title')}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-xl mb-4 text-blue-600 dark:text-blue-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('premium.benefits.multiAccount.title')}</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm">{t('premium.benefits.multiAccount.description')}</p>
          </Card>

          <Card className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/40 rounded-xl mb-4 text-green-600 dark:text-green-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('premium.benefits.schedulers.title')}</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm">{t('premium.benefits.schedulers.description')}</p>
          </Card>

          <Card className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/40 rounded-xl mb-4 text-purple-600 dark:text-purple-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('premium.benefits.sharing.title')}</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm">{t('premium.benefits.sharing.description')}</p>
          </Card>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 text-center mb-8">
          {t('premium.faq.title')}
        </h2>
        <div className="space-y-4">
          <Card>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('premium.faq.cancel.question')}</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm">{t('premium.faq.cancel.answer')}</p>
          </Card>
          <Card>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('premium.faq.data.question')}</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm">{t('premium.faq.data.answer')}</p>
          </Card>
          <Card>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('premium.faq.payment.question')}</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm">{t('premium.faq.payment.answer')}</p>
          </Card>
        </div>
      </div>

      {/* Upgrade Modal with PayPal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={handleUpgradeClose}
        onSuccess={handleUpgradeSuccess}
        monthlyPrice={monthlyPrice}
        annualMonthlyPrice={annualMonthlyEquivalent}
        annualTotalPrice={annualPrice}
        paypalClientId={config?.paypal?.clientId || ''}
        paypalPlanId={config?.paypal?.planId || ''}
        initialPaymentType={selectedPlan}
      />
    </div>
  );
};

export default PremiumPage;
